import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { utils, writeFile, Range } from "xlsx";
import {
  IHeadObjectXLS,
  TGeneratePdfFunction,
  TGenerateXLSFunction,
} from "./types";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { logo2Base64Png } from "constants/common";

const defaultFilename = "vindrives-file.pdf";

export const generateDownloadPdf: TGeneratePdfFunction = (
  tableConfig,
  filename,
  jsPDFConfig
) => {
  const doc = new jsPDF(jsPDFConfig);
  autoTable(doc, tableConfig);
  doc.save(filename || defaultFilename);
};

export const generatePrintPdf: TGeneratePdfFunction = (
  tableConfig,
  filename,
  jsPDFConfig
) => {
  const doc = new jsPDF(jsPDFConfig);

  doc.setProperties({
    title: filename || defaultFilename,
  });
  
  doc.autoPrint();
  autoTable(doc, tableConfig);

  // Print Get the PDF data as a data URI
  const pdfDataUri = doc.output("datauristring", {
    filename: filename || defaultFilename,
  });

  // Open the PDF file in a new window or iframe for printing
  const printWindow = window.open("", "_blank");
  printWindow?.document.write(
    `
        <style>
          body {
            margin: 0;
          }
        </style>
        <iframe src="${pdfDataUri}" style="width:100vw; height: 100vh;" frameborder="0" allowfullscreen></iframe>
      `
  );

  // Wait for the iframe content to load before printing
  printWindow?.document.addEventListener("load", () => {
    printWindow.print();
  });
};

export const generateEmailPdf: TGeneratePdfFunction = async (
  tableConfig,
  filename = defaultFilename
) => {
  const doc = new jsPDF();
  autoTable(doc, tableConfig);

  const title = `PDF Attachment ${filename}`;
  const body = "Please find the attached PDF file.";

  if ("share" in navigator) {
    const blobPDF = new Blob([doc.output()], { type: "application/pdf" });
    const shareData = {
      files: [
        new File([blobPDF], filename, {
          type: blobPDF.type,
        }),
      ],
      title,
      body,
    };

    if (navigator.canShare(shareData)) {
      await navigator.share(shareData);
    }
  } else {
    doc.save(filename || defaultFilename);

    // Create a mailto link with the subject, body, and PDF attachment
    var mailtoLink =
      "mailto:?subject=" +
      encodeURIComponent(title) +
      "&body=" +
      encodeURIComponent(body);

    // Open the default email application with the mailto link
    // hack for popup blocker
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const windowOpen = window.open(mailtoLink);
  }
};

// export const generateDownloadXLS: TGenerateXLSFunction = (
//   content,
//   fileName
// ) => {
//   const { body, head } = content;

//   // Fills up to the right empty strings based of the colspan value
//   const formattedHeaders = head.map((headRow) =>
//     headRow.reduce(
//       (newHeadContent, currentHeadContent): (string | IHeadObjectXLS)[] => {
//         let newFormat = [currentHeadContent];

//         if (
//           typeof currentHeadContent !== "string" &&
//           typeof currentHeadContent?.colspan === "number"
//         ) {
//           const blankSpaces = Array(currentHeadContent.colspan - 1).fill("_");
//           return [...newHeadContent, currentHeadContent, ...blankSpaces];
//         }

//         return [...newHeadContent, ...newFormat];
//       },
//       [] as (string | IHeadObjectXLS)[]
//     )
//   );

//   const mergeGroupHeaders: Range[] = [];

//   // Sets up the merged cells based on the colspan value from "content.head"
//   for (let indexRow = 0; indexRow < formattedHeaders.length; indexRow++) {
//     const headRow = formattedHeaders[indexRow];

//     for (let indexColumn = 0; indexColumn < headRow.length; indexColumn++) {
//       const headColumn = headRow[indexColumn];

//       if (
//         typeof headColumn === "object" &&
//         headColumn.hasOwnProperty("colspan") &&
//         typeof headColumn.colspan === "number"
//       ) {
//         formattedHeaders[indexRow][indexColumn] = headColumn.label;
//         mergeGroupHeaders.push({
//           s: { r: indexRow, c: indexColumn },
//           e: { r: indexRow, c: indexColumn + headColumn.colspan - 1 },
//         });
//       }
//     }
//   }

//   const worksheet = utils.aoa_to_sheet([...formattedHeaders, ...body]);

//   // Merges the cell headers
//   worksheet["!merges"] = mergeGroupHeaders;

//   /* create workbook and append worksheet */
//   const workbook = utils.book_new();
//   utils.book_append_sheet(workbook, worksheet, "Sheet 1");

//   /* export to XLSX */
//   writeFile(workbook, fileName);
// };

export const generateEmailXLS: TGenerateXLSFunction = (content, fileName) => {
  generateDownloadXLS(content, fileName);

  // Create a mailto link with the subject, body, and PDF attachment
  var mailtoLink =
    "mailto:?subject=" +
    encodeURIComponent(`PDF Attachment ${fileName}`) +
    "&body=" +
    encodeURIComponent("Please find the attached PDF file.");

  // Open the default email application with the mailto link
  // hack for popup blocker
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const windowOpen = window.open(mailtoLink);
};


// 👇 Replace this with your logo loading logic (can be base64, blob, or ArrayBuffer)
const getLogoBuffer = async (): Promise<ArrayBuffer> => {
  const response = await fetch(logo2Base64Png); // Your logo path or API
  return await response.arrayBuffer();
};

export const generateDownloadXLS: TGenerateXLSFunction = async (
  content,
  fileName
) => {
  const { body, head, formattedDate, title } = content;

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Sheet 1");

  // Dynamically calculate the total columns to fill the background
  const maxHeaderCols = Math.max(...head.map(row => {
    return row.reduce((count, col) => {
      return count + (typeof col === "object" && col.colspan ? col.colspan : 1);
    }, 0);
  }));

  // ✅ Insert empty rows at top to reserve space for logo
  let imagePaddingRows = 3;

  worksheet.spliceRows(1, 0, ...Array(imagePaddingRows).fill([]));

  // ✅ Add a black background "behind" the logo
  for (let i = 1; i <= imagePaddingRows; i++) {
    const row = worksheet.getRow(i);
    row.height = 25;
    for (let j = 1; j <= maxHeaderCols; j++) {
      const cell = row.getCell(j);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF000000" }, // black
      };
    }
  }

  // 2. Calculate center column for the image
  // const centerCol = Math.max(1, (maxHeaderCols / 2) - 1); // Offset for image width

  // // 3. Insert the image
  // const logoBuffer = await getLogoBuffer();
  // const imageId = workbook.addImage({
  //   buffer: logoBuffer,
  //   extension: "png",
  // });

  // worksheet.addImage(imageId, {
  //   tl: { col: centerCol, row: 0.3 }, // 👈 This will dynamically center it
  //   ext: { width: 200, height: 80 },
  // });

  if (title || formattedDate) {

    // Add an empty row immediately after the black rows
    imagePaddingRows += 1; // Row after the black background rows
    // worksheet.spliceRows(imagePaddingRows + 1, 0, []); // Insert an empty row
    const titleAndDateRow = worksheet.getRow(imagePaddingRows);
    
    titleAndDateRow.height = 25;
    
    // ✅ Add title and date row
    // Calculate the midpoint to split the row into 2 cells
    const midPoint = Math.ceil(maxHeaderCols / 2);

    if (title && formattedDate) {
      // Merge the first half of the columns into one cell
      worksheet.mergeCells(imagePaddingRows, 1, imagePaddingRows, midPoint);

      // Merge the second half of the columns into another cell
      worksheet.mergeCells(imagePaddingRows, midPoint + 1, imagePaddingRows, maxHeaderCols);

      // Add title to the first cell

      const titleCell = worksheet.getCell(imagePaddingRows, 1);
      const dateCell = worksheet.getCell(imagePaddingRows, midPoint + 1);

      titleCell.value = title;
      titleCell.alignment = { horizontal: "left", vertical: "middle", indent: 1 };
      titleCell.font = { bold: true, size: 14 };

      // Add date to the second cell
      dateCell.value = `${formattedDate}     `;
      dateCell.alignment = { horizontal: "right", vertical: "middle" };
      dateCell.font = { italic: true, size: 13 };
      // end of the title and date row
    } else {
      // Merge all columns into one cell
      worksheet.mergeCells(imagePaddingRows, 1, imagePaddingRows, maxHeaderCols);

      const titleCell = worksheet.getCell(imagePaddingRows, 1);
     
      titleCell.value = title;
      titleCell.alignment = { horizontal: "center", vertical: "middle" };
      titleCell.font = { bold: true, size: 14 };

    }
  }

  // ✅ Style header with offset
  head.forEach((headRow, rowIndex) => {
    let currentColIndex = 1;
    headRow.forEach((col: any) => {
      const colspan = typeof col === "object" && col.colspan ? col.colspan : 1;
      const startCol = currentColIndex;
      const endCol = currentColIndex + colspan - 1;
      const excelRow = imagePaddingRows + rowIndex + 1;

      for (let currentCol = startCol; currentCol <= endCol; currentCol++) {
        const cell = worksheet.getCell(excelRow, currentCol);
        
        // Apply background color
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFF0000" }, // Red background
        };

        // Apply font style
        cell.font = {
          color: { argb: "FFFFFFFF" }, // White text
          bold: true,
        };

        // Apply alignment
        cell.alignment = { horizontal: "center", vertical: "middle" };

        // Apply border
        cell.border = {
          top: { style: "thin", color: { argb: "FFB0B0B0" } }, // Gray border
          left: { style: "thin", color: { argb: "FFB0B0B0" } },
          bottom: { style: "thin", color: { argb: "FFB0B0B0" } },
          right: { style: "thin", color: { argb: "FFB0B0B0" } },
        };
      }

      currentColIndex = endCol + 1;
    });
  });

  // ✅ Handle merged headers with offset
  head.forEach((headRow, rowIndex) => {
    let currentColIndex = 1;
    headRow.forEach((col: any) => {
      const excelRow = imagePaddingRows + rowIndex + 1;
      if (typeof col === "object" && col.colspan) {
        const startCol = currentColIndex;
        const endCol = currentColIndex + col.colspan - 1;

        if (col.colspan > 1) {
          worksheet.mergeCells(excelRow, startCol, excelRow, endCol);
        }

        worksheet.getCell(excelRow, startCol).value = col.label;
        worksheet.getCell(excelRow, startCol).alignment = {
          horizontal: "center",
          vertical: "middle",
        };

        currentColIndex = endCol + 1;
      } else {
        worksheet.getCell(excelRow, currentColIndex).value = col;
        worksheet.getCell(excelRow, currentColIndex).alignment = {
          horizontal: "center",
          vertical: "middle",
        };

        currentColIndex++;
      }
    });
  });

  // ✅ Set row height for headers
  head.forEach((_, rowIndex) => {
    worksheet.getRow(imagePaddingRows + rowIndex + 1).height = 20;
  });

  // ✅ Add body rows with row height
  body.forEach((bodyRow, index) => {
    const row = worksheet.addRow(bodyRow);
    row.height = 20;
  });

  // ✅ Set column widths based on content
  worksheet.columns.forEach((column) => {
    const header = column.header?.toString() ?? "";
    const lengths: number[] = [header.length];
  
    column.eachCell?.({ includeEmpty: true }, (cell) => {
      const value = cell.value ? cell.value.toString() : "";
      lengths.push(value.length);
    });
  
    if (lengths.length === 0) {
      column.width = 10;
      return;
    }
  
    // Sort and get 95th percentile (to avoid large outliers)
    const sorted = lengths.sort((a, b) => a - b);
    const index = Math.floor(sorted.length * 0.95);
    const trimmedMax = sorted[index];
  
    // Calculate width: ensure header fits, set min and max bounds
    const paddedWidth = Math.max(trimmedMax, header.length) + 2;
    const minWidth = 20;
    const maxWidth = 40;
  
    column.width = Math.min(Math.max(paddedWidth, minWidth), maxWidth);
  });
  

  // ✅ Calculate the center column for the logo based on final column widths
  const totalWidth = worksheet.columns.reduce((sum, column) => sum + (column.width || 0), 0);

  let accumulatedWidth = 0;
  let centerCol = 1; // Default to the first column if no match is found
  for (let i = 0; i < worksheet.columns.length; i++) {
    accumulatedWidth += worksheet.columns[i].width || 0;
    if (accumulatedWidth >= totalWidth / 2) {
      centerCol = i + 1; // ExcelJS columns are 1-based
      break;
    }
  }

  // ✅ Insert the logo after column widths are finalized
  const logoBuffer = await getLogoBuffer();
  const imageId = workbook.addImage({
    buffer: logoBuffer,
    extension: "png",
  });

  worksheet.addImage(imageId, {
    tl: { col: centerCol - 1, row: 0.3 }, // Adjust for 0-based indexing
    ext: { width: 200, height: 80 },
  });

  // ✅ Generate and save file
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), fileName);
};